import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { lastmodFor } from "@/lib/route-lastmod";
import { LastUpdated } from "@/components/site/LastUpdated";
import { PHYSICIAN_REF } from "@/lib/medical-schema";
import { PageSection } from "@/components/site/PageSection";
import { ProcedureFaq } from "@/components/site/ProcedureFaq";
import { faqSchema, type Faq } from "@/components/site/InsuranceProse";
import poriesCartoon from "@/assets/pories-cartoon-dialysis-center-gastric-bypass.jpg.asset.json";
import { schemaDateFields } from "@/lib/schema-dates";


const PAGE_URL = "https://virginiabariatric.com/resources/obesity-and-diabetes";
const OG_IMAGE =
  "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg";
const TITLE = "Insulin Resistance and Bariatric Surgery | Virginia Bariatric";
const DESCRIPTION =
  "How obesity drives insulin resistance, how it is measured, and what randomized trials show about bariatric surgery, medications, and type 2 diabetes remission.";

const DIABETES_FAQS: Faq[] = [
  {
    q: "Can bariatric surgery cure type 2 diabetes?",
    a: "No. It can put diabetes into remission, sometimes for a decade or more, and it can improve insulin sensitivity dramatically. But remission can end, which is why we keep monitoring your blood sugar for life.",
  },
  {
    q: "How quickly does blood sugar improve after surgery?",
    a: "Often within days, well before significant weight has come off. Changes in gut hormone release drive that early effect. The weight loss that follows is what sustains it.",
  },
  {
    q: "Which operation works best for diabetes?",
    a: "Gastric bypass has the strongest evidence, with sleeve gastrectomy close behind. Adjustable gastric banding is the weakest of the three, because its metabolic benefit depends almost entirely on how much weight you lose.",
  },
  {
    q: "Do the weight-loss medications work as well as surgery?",
    a: "They work well, and for many patients they are the right first step. In head-to-head randomized trials against medical therapy, though, surgery has produced larger and more durable remission rates. The two are not mutually exclusive.",
  },
];

export const Route = createFileRoute("/resources/obesity-and-diabetes")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:type", content: "article" },
        { property: "og:url", content: PAGE_URL },
        { property: "og:image", content: OG_IMAGE },
        {
        property: "og:image:alt",
        content: "Virginia Bariatric Surgery — Dr. Matthew Fitzer's Northern Virginia weight-loss practice",
      },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: OG_IMAGE },
        {
        name: "twitter:image:alt",
        content: "Virginia Bariatric Surgery — Dr. Matthew Fitzer's Northern Virginia weight-loss practice",
      },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
      ] : []),
    ],
    links: [...canonicalAndAlternates("/resources/obesity-and-diabetes")],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Obesity, Insulin Resistance, and Bariatric Surgery",
          description: DESCRIPTION,
          datePublished: "2025-01-15",
          dateModified: lastmodFor("/resources/obesity-and-diabetes") ?? "2026-08-17",
          image: [
            OG_IMAGE,
            {
              "@type": "ImageObject",
              url: `https://virginiabariatric.com${poriesCartoon.url}`,
              caption:
                "“Grandpa, why didn't you get a gastric bypass?” Cartoon courtesy of Dr. Walter Pories, used with permission.",
              creditText: "Cartoon courtesy of Dr. Walter Pories, used with permission.",
              creator: { "@type": "Person", name: "Dr. Walter Pories" },
              copyrightNotice: "© Dr. Walter Pories, used with permission.",
              copyrightHolder: { "@type": "Person", name: "Dr. Walter Pories" },
              license: PAGE_URL,
              acquireLicensePage: PAGE_URL,
            },
          ],
          author: PHYSICIAN_REF,
          publisher: {
            "@type": "Organization",
            name: "Virginia Bariatric Surgery",
            url: "https://virginiabariatric.com",
            logo: {
              "@type": "ImageObject",
              url: "https://virginiabariatric.com/logo.png",
            },
          },
          mainEntityOfPage: PAGE_URL,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(faqSchema(DIABETES_FAQS, PAGE_URL)),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          ...schemaDateFields("/resources/obesity-and-diabetes"),
          "@id": `${PAGE_URL}#webpage`,
          url: PAGE_URL,
          name: "Obesity, Insulin Resistance, and Bariatric Surgery",
          description: DESCRIPTION,
          inLanguage: "en-US",
          isPartOf: { "@id": "https://virginiabariatric.com/#website" },
          about: [
            {
              "@type": "MedicalCondition",
              name: "Type 2 diabetes mellitus",
              code: { "@type": "MedicalCode", code: "E11", codingSystem: "ICD-10" },
            },
            {
              "@type": "MedicalCondition",
              name: "Obesity",
              code: { "@type": "MedicalCode", code: "E66", codingSystem: "ICD-10" },
            },
          ],
          audience: {
            "@type": "MedicalAudience",
            audienceType: "Patients with obesity and type 2 diabetes",
          },
          specialty: "Bariatric",
          reviewedBy: PHYSICIAN_REF,
        }),
      },
    ] : [],
  }),
  component: ObesityAndDiabetesPage,
});

const H2 = "text-2xl md:text-3xl font-serif text-brand-mid text-center pt-4";
const H3 = "text-xl md:text-2xl font-serif text-brand-mid text-center pt-4";

function ObesityAndDiabetesPage() {
  return (
    <>
      <PageSection className="pt-6 pb-4 text-center">
        <h1 className="text-4xl md:text-5xl font-serif text-brand-mid">
          Obesity, Insulin Resistance, and Bariatric Surgery
        </h1>
        <p className="mt-3 text-lg text-muted-foreground italic">
          One shared mechanism behind excess weight and type 2 diabetes
        </p>
      </PageSection>

      <PageSection maxWidth="4xl" className="pb-10">
        <article className="leading-[1.6] text-base md:text-lg space-y-5">
          <p>
            Type 2 diabetes and excess weight are not two separate problems that happen
            to travel together. They share a mechanism. Once you understand that
            mechanism, a lot of things make sense: why losing weight improves blood
            sugar, why some diabetes treatments work much faster than others, and why
            insulin resistance and bariatric surgery so often come up in the same
            conversation.
          </p>
          <p>
            This page covers what insulin resistance is, how it is measured, and what the
            evidence teaches us about reversing it.
          </p>

          <h2 className={H2}>How extra weight creates insulin resistance</h2>
          <p>
            Insulin signals to your body that it&rsquo;s time to move sugar out of the
            bloodstream and into your cells. <em>Insulin resistance</em> is what we call
            it when your cells stop listening to the insulin signal. Your pancreas tries
            to compensate for it by sending a louder signal. It releases more and more
            insulin. It keeps up&mdash;for a while. Insulin secretion climbs, and it
            keeps your blood glucose level near normal. Eventually, the day arrives when
            the pancreas can no longer keep up. That&rsquo;s when type 2 diabetes
            appears.
          </p>
          <p>
            Adipose tissue (fat) can be a potent driver of insulin resistance, but it
            depends where it is. Fat stored in the extremities (peripheral fat) is
            relatively harmless. In contrast, fat associated with your organs&mdash;the
            visceral fat&mdash;is very potent, metabolically speaking. It releases free
            fatty acids and other inflammatory signals that interfere with cells&rsquo;
            receptiveness to the insulin signal. Fat that collects in the liver is
            especially disruptive of glucose metabolism.
          </p>
          <p>
            The variable metabolic activity of different kinds of fat is why different
            people at the same body mass index (BMI) can have very different metabolic
            health. Location is more important than quantity.
          </p>

          <h2 className={H2}>How insulin resistance is measured</h2>
          <p>
            We use various tests to understand how well your body is managing blood sugar
            control. You may recognize them from your chart.
          </p>
          <p>
            Fasting blood glucose (FBG) and hemoglobin A1C levels (HbA1c) both provide a
            pretty good idea of your body&rsquo;s current ability to regulate glucose
            levels. Both have been used to diagnose type 2 diabetes.
          </p>
          <p>
            FBG has the benefit of convenience. You get an answer in five seconds, if you
            have a glucometer. It&rsquo;s also cheap. You can get a decent glucometer for
            $30 at Walmart.
          </p>
          <p>
            The HbA1c test requires sending a tube of blood to the lab, but it lets us
            determine your average blood glucose level for a period of about three
            months, which is an immensely useful piece of information. HbA1c recently
            replaced the glucose tolerance test as the gold standard for diagnosing type
            2 diabetes, and it has also become the standard measure of blood sugar
            control.
          </p>
          <p>
            The homeostasis model assessment, usually written HOMA-IR, estimates the
            resistance itself. It combines fasting glucose and fasting insulin into a
            single number. Researchers rely on it to compare groups at different time
            points before and after treatment, because it captures a shift in insulin
            sensitivity that HbA1c alone can miss.
          </p>
          <p>
            The oral glucose tolerance test measures how fast you clear a sugar load. It
            tells you something about both insulin resistance and insulin secretion.
          </p>

          <h2 className={H2}>What weight loss does</h2>
          <p>
            Losing weight reliably improves insulin sensitivity. Reduce visceral fat, and
            cells begin responding to insulin again. Blood sugars behave.
          </p>
          <p>
            The amount of weight loss is relevant to the improvement you see. Modest
            weight loss helps. Significant weight loss helps considerably more. Diet
            programs that are intensive enough (think six to eight weeks at a weight loss
            spa or inpatient treatment center) can put type 2 diabetes into remission
            without any surgery or medication at all.
          </p>
          <p>
            The problem with travel diet centers is that sooner or later, everybody goes
            home. Upon return to a normal routine, complete weight regain is the norm.{" "}
            <em>Sustained weight loss</em> is what determines whether diabetes remission
            lasts.
          </p>

          <h2 className={H2}>What weight-loss medications can do</h2>
          <p>
            The newer medications,{" "}
            <Link to="/medical-weight-loss/semaglutide" className="text-brand-mid underline">
              semaglutide
            </Link>{" "}
            and{" "}
            <Link to="/medical-weight-loss/tirzepatide" className="text-brand-mid underline">
              tirzepatide
            </Link>{" "}
            among them, were developed
            to treat type 2 diabetes before they were ever used for weight alone. They
            work on two fronts. They help you eat less, and they act directly on glucose
            metabolism.
          </p>
          <p>
            In trials, patients lost substantial weight and saw large improvements in
            blood glucose levels, and many were able to reduce or stop other diabetes
            medicines. These are ongoing treatments. They work while you take them, and
            blood sugar tends to drift back up if they are stopped.
          </p>

          <h2 className={H2}>
            Insulin resistance and bariatric surgery: what the trials show
          </h2>
          <p>
            Surgery has been tested head-to-head against medical therapy in randomized
            controlled trials, which is what makes this evidence unusually strong for a
            surgical field.
          </p>
          <h3 className={H3}>The STAMPEDE Trial</h3>
          <p>
            The STAMPEDE trial randomized 150 patients with type 2 diabetes and a BMI of
            27 to 43 to one of three arms: intensive medical therapy alone, or that same
            therapy plus{" "}
            <Link to="/services/bypass" className="text-brand-mid underline">
              gastric bypass surgery
            </Link>{" "}
            or{" "}
            <Link to="/services/sleeve" className="text-brand-mid underline">
              sleeve gastrectomy
            </Link>
            . At five years, 29%
            of the bypass group and 23% of the sleeve group had reached an HbA1c of 6.0%
            or lower, against 5% of those on medical therapy alone. Weight loss was 23%
            for bypass, 19% for sleeve, and 5% for medical therapy. Insulin use,
            triglycerides, HDL cholesterol, and quality-of-life scores all favored
            surgery.
          </p>
          <h3 className={H3}>The Rome Study (clever acronym unavailable)</h3>
          <p>
            A separate randomized controlled trial in Rome followed patients for ten
            years, the longest such data available. Diabetes remission at ten years was
            5.5% with medical therapy, 25% after gastric bypass, and 50% after
            biliopancreatic diversion.
          </p>
          <div className="md:flex md:items-stretch md:gap-6">
            <div className="md:flex-1 space-y-4">
              <p>
                Two findings deserve emphasis. Most patients who relapsed still held good
                glycemic control at ten years. And the surgical groups had dramatically
                fewer diabetes-related complications than the medically treated group.
              </p>
              <p>
                Results differ by procedure. Among obese individuals with type 2 diabetes,
                gastric bypass and sleeve gastrectomy both outperform adjustable gastric
                banding, which produces less weight loss and a correspondingly weaker
                metabolic effect.
              </p>
            </div>
            <figure className="mt-6 md:mt-0 md:flex md:w-72 md:shrink-0 md:flex-col md:min-h-0">
              <div className="md:min-h-0 md:flex-1 md:relative">
                <img
                  src={poriesCartoon.url}
                  alt="Cartoon by Dr. Walter Pories of a grandfather with diabetes complications outside a dialysis center, as his grandson asks why he didn't get a gastric bypass."
                  width={409}
                  height={392}
                  loading="lazy"
                  decoding="async"
                  className="w-full rounded-lg border border-border md:absolute md:inset-0 md:h-full md:object-contain"
                />
              </div>
              <figcaption className="mt-2 text-sm text-muted-foreground">
                &ldquo;Grandpa, why didn&rsquo;t you get a gastric bypass?&rdquo; Cartoon
                courtesy of Dr. Walter Pories, used with permission.
              </figcaption>
            </figure>
          </div>


          <h2 className={H2}>Why surgery acts faster than weight loss alone</h2>
          <p>
            Something happens in the first days after gastric bypass surgery that
            improves blood sugar before any meaningful weight has come off. Patients
            often go home on lower insulin doses than they came in with.
          </p>
          <p>
            Rerouting food changes how and when the gut releases hormones. GLP-1 and PYY
            rise, ghrelin falls. Those hormones sharpen insulin secretion after meals and
            blunt appetite. The weight loss that follows then does the slower work of
            restoring insulin sensitivity. The two effects stack, which is why this field
            increasingly calls the operations metabolic surgery rather than simply
            weight-loss surgery.
          </p>

          <h2 className={H2}>Remission is not always permanent</h2>
          <p>
            Remission is not the same as cure. In the ten-year data above, most patients
            who reached remission eventually saw blood sugar climb again, though they
            stayed better controlled than they had been before treatment.
          </p>
          <p>
            Two things predict durable results. The first is how long you had diabetes
            before treatment: patients treated earlier, before pancreatic function has
            declined far, do better. The second is whether the weight stays off. This is
            why we treat an operation as the start of long-term care rather than the end
            of treatment.
          </p>

          <h2 className={H2}>Who is a candidate</h2>
          <p>
            Current guidelines support surgery at a BMI of 35 or higher on weight alone,
            and at 30 to 34.9 when type 2 diabetes is present. For patients of East Asian
            descent, those thresholds drop to 32.5 and 27.5.
          </p>
          <p>
            Our{" "}
            <Link to="/resources/qualifications" className="text-brand-mid underline">
              bariatric surgery requirements
            </Link>{" "}
            page covers the full criteria, and our{" "}
            <Link
              to="/resources/insurance-coverage"
              className="text-brand-mid underline"
            >
              insurance coverage guide
            </Link>{" "}
            explains what the major plans approve.
          </p>

          <ProcedureFaq
            heading="Obesity and diabetes: frequently asked questions"
            faqs={DIABETES_FAQS}
          />

          <h2 className={H2}>Let's talk about it.</h2>
          <p>
            If you are living with type 2 diabetes and carrying excess weight, you have
            more options than you did a decade ago, and better evidence behind them.
            Request a consultation and we will walk through medication, surgery, and what
            a combination of the two might look like for you.
          </p>
        </article>

        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link
            to="/appointments"
            className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Request a consultation
          </Link>
          <Link
            to="/resources/qualifications"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Bariatric Surgery Requirements
          </Link>
          <Link
            to="/resources/insurance-coverage"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Insurance Coverage Guide
          </Link>
        </div>

        <section className="mt-12 border-t pt-6">
          <h2 className="text-xl font-serif text-brand-mid mb-3">References</h2>
          <ol className="list-decimal pl-6 space-y-2 text-sm text-muted-foreground leading-snug">
            <li>
              Schauer PR, Bhatt DL, Kirwan JP, et al. Bariatric Surgery versus Intensive
              Medical Therapy for Diabetes &mdash; 5-Year Outcomes (STAMPEDE).{" "}
              <em>N Engl J Med.</em> 2017;376(7):641-651. doi:10.1056/NEJMoa1600869
            </li>
            <li>
              Mingrone G, Panunzi S, De Gaetano A, et al. Metabolic surgery versus
              conventional medical therapy in patients with type 2 diabetes: 10-year
              follow-up of an open-label, single-centre, randomised controlled trial.{" "}
              <em>Lancet.</em> 2021;397(10271):293-304.
              doi:10.1016/S0140-6736(20)32649-0
            </li>
          </ol>
        </section>
      <LastUpdated className="mt-10" />
      </PageSection>
    </>
  );
}
